import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export const TRUE_FALSE_OPTION_LABELS = {
  true: "Wahr",
  false: "Falsch",
} as const;

export const TRUE_FALSE_OPTIONS_LAYOUT =
  "flex w-full flex-col items-center gap-4";

export const TRUE_FALSE_OPTIONS_GRID = "grid w-80 max-w-full grid-cols-2 gap-4";

export interface AnswerButtonProps {
  value: boolean;
  label: string;
  icon: typeof Check | typeof X;
  isSelected: boolean;
  isSubmitted: boolean;
  isCorrect: boolean | null;
  onSelect: (value: boolean) => void;
}

const optionSurfaceClass = (
  isSubmitted: boolean,
  isSelected: boolean,
  isCorrect: boolean | null
) => {
  if (isSubmitted && isSelected && isCorrect) {
    return "border-2 border-success bg-success-light";
  }
  if (isSubmitted && isSelected) {
    return "border-2 border-error bg-error-light";
  }
  if (isSelected) {
    return "border-2 border-secondary bg-secondary/10";
  }
  return "border-border bg-card";
};

const optionIconClass = (isSelected: boolean, isSubmitted: boolean) => {
  if (isSelected && !isSubmitted) {
    return "text-secondary";
  }
  return "text-muted-foreground";
};

const optionLabelClass = (isSelected: boolean, isSubmitted: boolean) => {
  if (isSelected && !isSubmitted) {
    return "text-secondary";
  }
  return "text-primary";
};

/**
 * Compact Wahr/Falsch choice — used by true/false quizzes, matching the
 * in-lesson Lernkarte card surface.
 */
export const AnswerButton = ({
  value,
  label,
  icon: Icon,
  isSelected,
  isSubmitted,
  isCorrect,
  onSelect,
}: AnswerButtonProps) => (
  <button
    type="button"
    aria-pressed={isSelected}
    onClick={() => onSelect(value)}
    disabled={isSubmitted}
    className={cn(
      "flex aspect-5/4 w-full flex-col items-center justify-center gap-2 rounded-2xl border p-3",
      "transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
      "enabled:hover:border-secondary/50 disabled:cursor-not-allowed",
      optionSurfaceClass(isSubmitted, isSelected, isCorrect)
    )}
  >
    <Icon
      className={cn(
        "size-8 transition-colors",
        optionIconClass(isSelected, isSubmitted)
      )}
      strokeWidth={1.5}
    />
    <span
      className={cn("font-semibold", optionLabelClass(isSelected, isSubmitted))}
    >
      {label}
    </span>
  </button>
);
